import React, {
  forwardRef,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { fontWeights } from '../../theme';
import { mergeRefs } from '../../utils';

import {
  ChildContainer,
  ListItemRoot,
  LeftContainer,
  RightContainer,
} from './styled';

export type ListItemProps = HTMLAttributes<HTMLLIElement> & {
  left?: ReactNode;
  children?: React.ReactNode;
  right?: ReactNode;
  isDisabled?: boolean;
  isSelected?: boolean;

  /**
   * You can use this prop if you want to make element look like it's focused without it being actually focused
   * */
  isHighlighted?: boolean;

  /**
   * By default component is using background-color from component theming,
   * however sometimes you want parent to control color, in which case you can use this prop
   * */
  hasTransparentBackground?: boolean;
};

const ListItem = forwardRef<HTMLLIElement, ListItemProps>(
  (props, forwardedRef) => {
    const {
      children,
      left,
      right,
      onClick,
      onKeyDown,
      isDisabled,
      isSelected,
      hasTransparentBackground = false,
      isHighlighted,
      tabIndex = 0,
      ...rest
    } = props;
    const ref = useRef<HTMLLIElement>(null);

    const clickProps: HTMLAttributes<HTMLLIElement> =
      onClick && !isDisabled
        ? {
            tabIndex,
            onClick,
            onKeyDown: e => {
              onKeyDown?.(e);

              if (e.isDefaultPrevented()) return;

              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                ref.current?.click();
              }
            },
          }
        : {};

    return (
      <ListItemRoot
        $hasTransparentBackground={hasTransparentBackground}
        $isHighlighted={isHighlighted}
        aria-disabled={isDisabled}
        aria-selected={isSelected}
        ref={mergeRefs(ref, forwardedRef)}
        role="option"
        {...rest}
        {...clickProps}>
        <LeftContainer>{left}</LeftContainer>
        <ChildContainer
          $fontWeight={isSelected ? fontWeights.medium : undefined}>
          {children}
        </ChildContainer>
        <RightContainer>{right}</RightContainer>
      </ListItemRoot>
    );
  },
);

ListItem.displayName = 'ListItem';

export default ListItem;
